import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtINR } from "@/lib/leadUtils";

const PENDING_BANNER_DISMISSED_KEY = "overhead-pending-dismissed";
// Show banner only within first N days of month (spec §4.3)
const SHOW_DAYS = 7;

function dayOfMonth() {
  return new Date().getDate();
}

function monthLabel(mtag) {
  if (!mtag || mtag.length < 7) return mtag;
  const [y, m] = mtag.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString(undefined, { month: "long", year: "numeric" });
}

export default function PendingTemplatesBanner({ monthTag, onConfirmed }) {
  const [pending, setPending] = useState([]);
  const [amounts, setAmounts] = useState({});
  const [submittingId, setSubmittingId] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(`${PENDING_BANNER_DISMISSED_KEY}:${monthTag}`) === "1";
    } catch { return false; }
  });

  // Only show within first SHOW_DAYS of CURRENT month (not historical month picks).
  const monthTagToday = new Date().toISOString().slice(0, 7);
  const withinWindow = monthTag === monthTagToday && dayOfMonth() <= SHOW_DAYS;

  const refresh = async () => {
    if (!withinWindow) { setPending([]); return; }
    try {
      const { data } = await api.get("/expense-templates/pending", { params: { month_tag: monthTag } });
      const items = data.items || [];
      setPending(items);
      const seed = {};
      for (const t of items) seed[t.id] = String(t.default_amount || "");
      setAmounts(seed);
    } catch {
      setPending([]);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [monthTag]);

  const confirmOne = async (tmpl) => {
    const amt = Number(amounts[tmpl.id]);
    if (!amt || amt <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setSubmittingId(tmpl.id);
    try {
      await api.post(`/expense-templates/${tmpl.id}/confirm`, { month_tag: monthTag, amount: amt });
      toast.success(`${tmpl.name} confirmed for ${monthLabel(monthTag)}`);
      // Remove from list optimistically
      setPending((items) => items.filter((t) => t.id !== tmpl.id));
      onConfirmed && onConfirmed();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not confirm");
    } finally {
      setSubmittingId(null);
    }
  };

  const dismiss = () => {
    try { sessionStorage.setItem(`${PENDING_BANNER_DISMISSED_KEY}:${monthTag}`, "1"); } catch {}
    setDismissed(true);
  };

  if (!withinWindow || dismissed || pending.length === 0) return null;

  return (
    <div className="bg-amber-50 border border-amber-300 p-4 mb-4" data-testid="pending-templates-banner">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="kicker mb-1" style={{ color: "var(--brand-champagne)" }}>PENDING STUDIO OVERHEAD</div>
          <div className="text-sm" style={{ color: "var(--brand-ink)" }}>
            {pending.length} template{pending.length > 1 ? "s" : ""} to confirm for {monthLabel(monthTag)}
          </div>
        </div>
        <button
          onClick={dismiss}
          className="text-[11px] tracking-[0.18em] uppercase text-brand-muted"
          data-testid="pending-banner-dismiss"
        >
          Dismiss
        </button>
      </div>
      <ul className="space-y-2">
        {pending.map((t) => (
          <li key={t.id} className="flex items-center gap-2" data-testid={`pending-template-${t.id}`}>
            <div className="flex-1 text-sm">
              <span className="font-medium">{t.name}</span>
              <span className="text-xs text-brand-muted ml-2">{t.category} · last {fmtINR(t.default_amount)}</span>
            </div>
            <input
              type="number"
              value={amounts[t.id] || ""}
              onChange={(e) => setAmounts({ ...amounts, [t.id]: e.target.value })}
              className="w-32 px-2 py-1.5 text-sm border border-brand-border"
              style={{ borderRadius: 0 }}
              data-testid={`pending-template-amount-${t.id}`}
            />
            <button
              onClick={() => confirmOne(t)}
              disabled={submittingId === t.id}
              className="px-3 py-1.5 text-[11px] tracking-[0.18em] uppercase text-white"
              style={{ background: "var(--brand-primary)" }}
              data-testid={`pending-template-confirm-${t.id}`}
            >
              {submittingId === t.id ? "…" : "Confirm"}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
